import cn from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, Loader, Select, Slider } from 'src/components';
import { useAlerts } from 'src/common/AlertManager';
import CloudArrowUp from 'src/common/icons/CloudArrowUp';
import {
  useUploadAdminUserWatermarkMutation,
  useUpdateAdminUserSettingsMutation,
} from 'src/api/mutations/adminUserSettingsMutations';
import { config } from 'src/utils/config';

import styles from '../styles.module.scss';

interface WatermarkTabProps {
  userId: number;
  userSettings: any;
}

const PLACEMENT_OPTIONS = [
  { value: 'top-left', label: 'Top left corner' },
  { value: 'top-right', label: 'Top right corner' },
  { value: 'bottom-left', label: 'Bottom left corner' },
  { value: 'bottom-right', label: 'Bottom right corner' },
  { value: 'center', label: 'Center' },
];

const WatermarkTab: React.FC<WatermarkTabProps> = ({ userId, userSettings }) => {
  const { showSuccess, showAlert } = useAlerts();

  const uploadMutation = useUploadAdminUserWatermarkMutation(userId);
  const updateSettingsMutation = useUpdateAdminUserSettingsMutation(userId);

  const watermarkData = userSettings?.watermark || {};
  const [watermarkFileKey, setWatermarkFileKey] = useState<string | null>(null);
  const [placement, setPlacement] = useState(watermarkData.watermarkPlacement || 'bottom-right');
  const [scale, setScale] = useState(watermarkData.watermarkScale || 0.2);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const watermarkPreviewRef = useRef<HTMLImageElement>(null);
  const watermarkBackgroundRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (watermarkData.watermarkPlacement) setPlacement(watermarkData.watermarkPlacement);
    if (watermarkData.watermarkScale) setScale(watermarkData.watermarkScale);
  }, [watermarkData.watermarkPlacement, watermarkData.watermarkScale]);

  const hasChanges =
    watermarkFileKey !== null ||
    placement !== (watermarkData.watermarkPlacement || 'bottom-right') ||
    scale !== (watermarkData.watermarkScale || 0.2);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const [file] = acceptedFiles;
    if (!file) return;

    setIsUploading(true);
    try {
      const result = await uploadMutation.mutateAsync(file);
      setWatermarkFileKey(result.watermarkFileKey);
      setLocalPreview(URL.createObjectURL(file));
      showSuccess('Watermark uploaded');
    } catch {
      showAlert('Failed to upload watermark');
    } finally {
      setIsUploading(false);
    }
  }, [userId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png',
    maxFiles: 1,
    maxSize: 500 * 1024,
  });

  const handleSave = async () => {
    const value: any = {
      watermarkPlacement: placement,
      watermarkScale: scale,
    };
    if (watermarkFileKey) {
      value.watermarkFileKey = watermarkFileKey;
    }

    try {
      await updateSettingsMutation.mutateAsync({ key: 'watermark', value });
      setWatermarkFileKey(null);
      showSuccess('Watermark settings saved');
    } catch {
      showAlert('Failed to save watermark settings');
    }
  };

  useEffect(() => {
    if (!watermarkPreviewRef.current) return;

    const el = watermarkPreviewRef.current;
    el.style.top = '';
    el.style.right = '';
    el.style.bottom = '';
    el.style.left = '';
    el.style.transform = '';

    switch (placement) {
      case 'top-left':
        el.style.top = '0';
        el.style.left = '0';
        break;
      case 'top-right':
        el.style.top = '0';
        el.style.right = '0';
        break;
      case 'bottom-left':
        el.style.bottom = '0';
        el.style.left = '0';
        break;
      case 'bottom-right':
        el.style.bottom = '0';
        el.style.right = '0';
        break;
      case 'center':
        el.style.top = '50%';
        el.style.left = '50%';
        el.style.transform = 'translate(-50%, -50%)';
        break;
    }
  }, [placement]);

  const setWatermarkHeightSize = () => {
    if (!watermarkPreviewRef.current || !watermarkBackgroundRef.current) return;
    watermarkPreviewRef.current.style.height = `${watermarkBackgroundRef.current.height * scale}px`;
  };

  useEffect(() => {
    setWatermarkHeightSize();
  }, [scale]);

  const timestamp = Date.now();
  const previewSrc = localPreview || (watermarkData.watermarkFile ? `${config.hostUrl}${watermarkData.watermarkFile}?_cb=${timestamp}` : '');

  return (
    <div>
      <h4 className={styles.sectionSubTitle}>Watermark file</h4>

      <div className={styles.watermarkFile}>
        <div className={cn(styles.dragAndDrop, isDragActive ? styles.dragging : undefined)} {...getRootProps()}>
          <input {...getInputProps()} />
          <CloudArrowUp fontSize={28} />
          <h4>
            Drag your image here or <span>browse</span>
          </h4>
          <h5>JPG or PNG, max 1012x768 px, max 500 KB</h5>
        </div>
        <div className={styles.previewWatermark}>
          <div>
            {isUploading ? (
              <Loader />
            ) : previewSrc ? (
              <img src={previewSrc} alt="watermark" />
            ) : (
              <span style={{ color: 'var(--color-gray-400)', fontSize: 'var(--font-size-s)' }}>No watermark</span>
            )}
          </div>
        </div>
      </div>

      <h4 className={styles.sectionSubTitle}>Options</h4>

      <div className={styles.watermarkOptionsRow}>
        <div>
          <h5>Watermark placement</h5>
          <Select
            value={placement}
            onChange={(value: string) => setPlacement(value)}
            options={PLACEMENT_OPTIONS}
          />
          <p>Where to place the watermark on conversions and transcodes</p>
        </div>
        <div>
          <h5>Watermark Scaling</h5>
          <Slider
            min="0.01"
            max="1.0"
            step="0.01"
            value={scale}
            onChange={(value: number) => setScale(value)}
          />
          <p>A value of 0.01 places a watermark at 1% of the target size</p>
        </div>
      </div>

      {previewSrc && (
        <div className={styles.watermarkPreview}>
          <img ref={watermarkBackgroundRef} src="/wm_preview_bg.png" alt="preview background" onLoad={setWatermarkHeightSize} />
          <img
            ref={watermarkPreviewRef}
            className={styles.watermarkPositionPreview}
            src={previewSrc}
            alt="watermark position"
          />
        </div>
      )}

      <div className={styles.watermarkActions}>
        <Button
          mode="primary"
          size="small"
          onClick={handleSave}
          disabled={!hasChanges || updateSettingsMutation.isLoading}
        >
          Save Watermark Settings
        </Button>
      </div>
    </div>
  );
};

export default WatermarkTab;
